/**
 * API hooks для unified inbox и proposal review.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { SkipReason, ObserverSignals } from '../types';
import { client } from './client';

// ============================================================================
// Types
// ============================================================================

export interface InboxItem {
  id: string;
  channel: 'email' | 'telegram' | 'widget';
  title: string;
  snippet: string;
  lastMessageAt: string;
  messageCount: number;
  hasDraft: boolean;
  needsReply: boolean;
  hasFailedMessages: boolean;
  status: 'active' | 'resolved' | 'archived';
}

interface InboxResponse {
  items: InboxItem[];
  total: number;
  limit: number;
  offset: number;
}

interface InboxParams {
  channel?: 'email' | 'telegram' | 'widget' | 'all';
  status?: 'active' | 'has-draft' | 'all';
  limit?: number;
  offset?: number;
}

// ============================================================================
// Inbox
// ============================================================================

export function useInbox(params: InboxParams = {}): UseQueryResult<InboxResponse, Error> {
  return useQuery({
    queryKey: ['inbox', params],
    queryFn: async () => {
      const { data } = await client.get<InboxResponse>('/inbox', { params });
      return data;
    },
    // 10 секунд: список показывает новые обращения, и полминуты — слишком долго,
    // чтобы заметить клиента. Один запрос в 10 секунд для лимита незаметен.
    refetchInterval: 10_000,
  });
}

// ============================================================================
// Proposal Review
// ============================================================================

export function useAcceptProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await client.post(`/proposals/${proposalId}/accept`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, reason }: { proposalId: string; reason?: string }) => {
      const { data } = await client.post(`/proposals/${proposalId}/reject`, { reason });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

export function useEditProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, text }: { proposalId: string; text: string }) => {
      const { data } = await client.patch(`/proposals/${proposalId}/edit`, { text });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

/**
 * Попросить черновик руками — кнопка «Подготовить ответ».
 *
 * Один эндпоинт на три канала. До 09.09 существовал только телеграмный, и
 * интерфейс его не звал вовсе; сам он к тому же молча не выполнялся при
 * повторе.
 *
 * Черновик по этой кнопке НИКОГДА не отправляется автоматически, даже когда
 * диалог стоит в режиме бота: оператор просил показать текст.
 */
export function useGenerateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceType,
      sourceId,
    }: {
      sourceType: 'email' | 'telegram' | 'widget';
      /** Диалог: тред письма, чат телеграма, сессия виджета. */
      sourceId: string;
    }) => {
      const { data } = await client.post('/proposals/generate', { sourceType, sourceId });
      return data as { ok: boolean; jobId: string; replacedPrevious: boolean; message: string };
    },
    onSuccess: () => {
      // Черновик появится не сразу — его готовит воркер. Опрос списка
      // черновиков уже учащается, пока есть `thinking`
      qc.invalidateQueries({ queryKey: ['proposals'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

// ============================================================================
// Proposals для конкретного диалога
// ============================================================================

export interface ProposalItem {
  id: string;
  sourceType: 'email' | 'telegram' | 'widget';
  sourceId: string;
  sourceMessageId: string | null;
  sourceTurnId: string;
  text: string;
  /** Что предложил движок до правки оператора. `null` = не правили. */
  originalText: string | null;
  status: 'thinking' | 'ready' | 'skipped' | 'matched';
  shadowDecision: string | null;
  /** Почему черновика нет. Показывается оператору словами. */
  skipReason: SkipReason | null;
  /** Сигналы наблюдателя. Виджет их пока не отдаёт — отсюда необязательность. */
  observerSignals?: ObserverSignals | null;
  strategy: string | null;
  matchScore: number | null;
  matchCategory: string | null;
  generationContext: Record<string, unknown> | null;
  createdAt: string;
}

interface ProposalsResponse {
  proposals: ProposalItem[];
}

export function useDialogueProposals(
  sourceType: 'email' | 'telegram' | 'widget',
  sourceId: string,
  enabled = true,
): UseQueryResult<ProposalItem[], Error> {
  return useQuery({
    queryKey: ['proposals', sourceType, sourceId],
    queryFn: async () => {
      if (sourceType === 'email') {
        const { data } = await client.get<ProposalsResponse>(`/threads/${sourceId}/proposals`);
        return data.proposals;
      }
      if (sourceType === 'widget') {
        const { data } = await client.get<ProposalsResponse>(`/widget/sessions/${sourceId}/proposals`);
        return data.proposals;
      }
      // Telegram — используем существующий endpoint из telegram-lab
      const { data } = await client.get<ProposalsResponse>(`/telegram/chats/${sourceId}/proposals`);
      return data.proposals;
    },
    enabled,
    // Пока ждём черновик — опрашиваем часто, иначе индикатор «AI готовит
    // черновик» висел бы до 15 секунд после того, как ответ уже готов.
    // Ждём, если есть proposal в статусе thinking или их пока нет вовсе.
    refetchInterval: (query) => {
      const proposals = query.state.data;
      if (!proposals || proposals.length === 0) return 2_000;
      return proposals.some((p) => p.status === 'thinking') ? 1_000 : 15_000;
    },
  });
}
